import "@fontsource/poppins";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";

import Box from "@mui/material/Box";
import "bootstrap/dist/css/bootstrap.css";
import "font-awesome/css/font-awesome.min.css";
import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./App.css";
import "./fonts.css";

import Footer from "./components/common/footer";
import ResponsiveAppBar from "./components/common/new-nav-bar";

import PaypalSuccess from "./components/pages/PaypalSuccess";
import ChangePassForm from "./components/pages/change-pass";
import Downloads from "./components/pages/downloads";
import EventsPage from "./components/pages/events-page";
import { FinderPage } from "./components/pages/finder-page";
import ForgotPassword from "./components/pages/forgot-password";
import Home from "./components/pages/home";
import LoginPage from "./components/pages/login-page";
import Logout from "./components/pages/logout";
import { MarketPlacePage } from "./components/pages/marketplace";
import { PlayerProfile } from "./components/pages/player-profile";
import UserProfile from "./components/pages/profile";
import RankingsPage from "./components/pages/ranks";
import RegisterPage from "./components/pages/register-page";
import ResetPassword from "./components/pages/reset-password";
import { ServerNewsPage } from "./components/pages/server-news-page";

import Terms from "./components/pages/Terms";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import RefundPolicy from "./components/pages/RefundPolicy";

import StorePage from "./components/pages/StorePage";
import WikiPage from "./components/pages/WikiPage";
import DiscordJoinPage from "./components/pages/DiscordJoinPage";

import AuthService from "./services/authService";
import { useInterceptor } from "./services/axios-interceptors";

const App = () => {
    useInterceptor();

    const location = useLocation();
    const isHomePage = location.pathname === "/";

    // PROTECTED ROUTES
    const ProtectedRouteLoggedIn = ({ children }) => {
        if (!AuthService.isUserLoggedIn()) {
            return <Navigate to="/user/login" replace />;
        }
        return children;
    };

    const ProtectedRouteNotLogged = ({ children }) => {
        if (AuthService.isUserLoggedIn()) {
            return <Navigate to="/" replace />;
        }
        return children;
    };

    return (
        <Box
            sx={{
                position: "relative",
                minHeight: "100vh",
                overflowX: "hidden",
                background:
                    "linear-gradient(to bottom, #070707, #0b0b0b)",
            }}
        >
            <ToastContainer
                position="top-center"
                autoClose={3500}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                draggable
                theme="dark"
                style={{
                    marginTop: "100px",
                }}
                toastStyle={{
                    background:
                        "linear-gradient(135deg, #111111, #1b1b1b)",
                    color: "#ffffff",
                    border: "1px solid rgba(212,164,55,.35)",
                    borderRadius: "16px",
                    boxShadow:
                        "0 15px 40px rgba(0,0,0,.45)",
                    fontWeight: 600,
                    minHeight: "70px",
                }}
                progressStyle={{
                    background:
                        "linear-gradient(90deg,#D4A437,#f0c75a)",
                }}
            />

            {/* NAVBAR */}
            <ResponsiveAppBar isHomePage={isHomePage} />

            {/* MAIN CONTENT */}
            <Box sx={{ position: "relative", zIndex: 2 }}>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/downloads" element={<Downloads />} />
                    <Route path="/ranks" element={<RankingsPage />} />
                    <Route path="/updates" element={<ServerNewsPage />} />
                    <Route path="/schedules" element={<EventsPage />} />
                    <Route path="/finder" element={<FinderPage />} />
                    <Route path="/marketplace" element={<MarketPlacePage />} />
                    <Route path="/wiki" element={<WikiPage />} />
                    <Route path="/discord" element={<DiscordJoinPage />} />

                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/refund-policy" element={<RefundPolicy />} />

                    <Route
                        path="/payment/paypal-success"
                        element={
                            <ProtectedRouteLoggedIn>
                                <PaypalSuccess />
                            </ProtectedRouteLoggedIn>
                        }
                    />

                    <Route
                        path="/store"
                        element={
                            <ProtectedRouteLoggedIn>
                                <StorePage />
                            </ProtectedRouteLoggedIn>
                        }
                    />

                    <Route
                        path="/user/login"
                        element={
                            <ProtectedRouteNotLogged>
                                <LoginPage />
                            </ProtectedRouteNotLogged>
                        }
                    />

                    <Route path="/user/logout" element={<Logout />} />
                    <Route path="/user/profile" element={<UserProfile />} />

                    <Route
                        path="/user/change-password"
                        element={
                            <ProtectedRouteLoggedIn>
                                <ChangePassForm />
                            </ProtectedRouteLoggedIn>
                        }
                    />

                    <Route path="/user/register" element={<RegisterPage />} />
                    <Route path="/user/forgot-password" element={<ForgotPassword />} />
                    <Route path="/recoverpassword" element={<ResetPassword />} />

                    <Route path="/player-profile/:name" element={<PlayerProfile />} />

                    {/* 404 fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Box>

            {/* FOOTER */}
            <Footer />
        </Box>
    );
};

export default App;